"use client";

import React, { useCallback, useEffect, useState } from "react";
import { getBackendRenderAttributes, isBackendRenderMode } from "@/lib/backend-render-utils";
import { useCustomerPanelTranslations } from "@/lib/customer-panel-utils";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import CustomerApiService, { type InvoiceListItem } from "@/services/CustomerApiService";
import type { PageUrlMap } from "@/types/blog";

interface CustomerInvoicesProps {
  language?: string;
  translations?: Record<string, string>;
  pageUrls?: PageUrlMap;
  defaultLanguage?: string;
  __grapesPreview?: boolean;
}

function formatDate(value: string | null | undefined, locale: string): string {
  if (!value) return '—';
  try {
    const d = new Date(value);
    const loc = locale === 'tr' ? 'tr-TR' : 'en-GB';
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(loc, { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '—';
  }
}

function formatAmount(amount: string | number | undefined, symbol: string | undefined, locale: string): string {
  if (amount === undefined || amount === null) return '—';
  const n = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (Number.isNaN(n)) return '—';
  const loc = locale === 'tr' ? 'tr-TR' : 'en-GB';
  return n.toLocaleString(loc, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + (symbol ? ` ${symbol}` : '');
}

export default function CustomerInvoices({
  language = 'en',
  translations = {},
  pageUrls = {},
  defaultLanguage = 'en',
  __grapesPreview = false,
  ...restProps
}: CustomerInvoicesProps) {
  const isBackendRender = isBackendRenderMode({ __grapesPreview, ...restProps });
  const backendAttributes = getBackendRenderAttributes(
    'page_layouts/CustomerInvoices',
    { language, translations, pageUrls },
    isBackendRender
  );

  const t = useCustomerPanelTranslations(translations);
  const { getVariable } = useLanguageVariables();
  const { isAuthenticated, customer, isLoading: authLoading } = useCustomerAuth();

  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [meta, setMeta] = useState<{ pagination?: { page: number; pageCount: number; total: number } } | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [payingId, setPayingId] = useState<number | null>(null);

  useEffect(() => {
    if (isBackendRender || !isAuthenticated) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    CustomerApiService.getInvoices({ page, per_page: 10 })
      .then((res) => {
        if (!cancelled) {
          setInvoices(res.data);
          setMeta(res.meta);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = translations['customer_panel.invoices.load_error']
            || getVariable('customer_panel.invoices.load_error', '');
          setError(err instanceof Error ? err.message : msg);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isBackendRender, isAuthenticated, page, translations, getVariable]);

  const handlePayInvoice = useCallback(async (id: number) => {
    setPayingId(id);
    try {
      const { payment_url } = await CustomerApiService.getInvoicePaymentLink(id);
      window.location.href = payment_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : t('customer_panel.invoices.load_error', 'Could not open payment'));
    } finally {
      setPayingId(null);
    }
  }, [t]);

  const handleDownloadPdf = useCallback(async (id: number) => {
    setDownloadingId(id);
    try {
      const { pdf_url } = await CustomerApiService.getInvoicePdfInfo(id);
      window.open(pdf_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error(err);
    } finally {
      setDownloadingId(null);
    }
  }, []);

  if (!isBackendRender && (!isAuthenticated || !customer) && !authLoading) {
    return null;
  }

  const pagination = meta?.pagination;
  const total = pagination?.total ?? 0;
  const pageCount = pagination?.pageCount ?? 1;
  const currentPage = pagination?.page ?? 1;

  return (
    <div {...backendAttributes}>
      <div className="mb-6 pb-4 border-b border-gray-100">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          {t('customer_panel.invoices.title')}
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          {t('customer_panel.invoices.subtitle')}
        </p>
      </div>

      {isBackendRender && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <p className="text-gray-600">{t('customer_panel.invoices.empty')}</p>
        </div>
      )}

      {!isBackendRender && (
        <>
          {loading && (
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <th key={i} className="px-4 py-3">
                        <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {[1, 2, 3, 4].map((row) => (
                    <tr key={row}>
                      {[1, 2, 3, 4, 5].map((i) => (
                        <td key={i} className="px-4 py-3">
                          <div className={`h-4 rounded bg-gray-200 animate-pulse ${i === 1 ? 'w-28' : i === 4 ? 'w-16 ml-auto' : 'w-24'}`} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {error && (
            <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">{error}</div>
          )}

          {!loading && !error && invoices.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <p className="text-gray-600">{t('customer_panel.invoices.empty')}</p>
            </div>
          )}

          {!loading && !error && invoices.length > 0 && (
            <div className="space-y-4">
              <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('customer_panel.invoices.invoice_no')}
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('customer_panel.invoices.issue_date')}
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('customer_panel.invoices.status')}
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('customer_panel.invoices.total')}
                      </th>
                      {invoices.some((inv) => inv.is_payable && (inv.remaining_amount ?? 0) > 0) && (
                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('customer_panel.invoices.remaining')}
                        </th>
                      )}
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('customer_panel.invoices.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {invoices.map((inv) => (
                      <tr key={inv.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{inv.invoice_no ?? '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDate(inv.issue_date, language)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                            inv.status === 'paid' ? 'bg-green-100 text-green-800' :
                            inv.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-amber-100 text-amber-800'
                          }`}>
                            {inv.status === 'paid' ? t('customer_panel.invoices.status_paid') :
                             inv.status === 'pending' ? t('customer_panel.invoices.status_pending') :
                             inv.status === 'draft' ? t('customer_panel.invoices.status_draft') :
                             inv.status === 'cancelled' ? t('customer_panel.invoices.status_cancelled') :
                             inv.status ?? '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">
                          {formatAmount(inv.total_amount, inv.currency?.symbol, language)}
                        </td>
                        {invoices.some((i) => i.is_payable && (i.remaining_amount ?? 0) > 0) && (
                          <td className="px-4 py-3 text-sm text-right text-gray-600">
                            {inv.is_payable && (inv.remaining_amount ?? 0) > 0
                              ? formatAmount(inv.remaining_amount, inv.currency?.symbol, language)
                              : '—'}
                          </td>
                        )}
                        <td className="px-4 py-3 text-right space-x-2">
                          {inv.is_payable && (inv.remaining_amount ?? 0) > 0 && (
                            <button
                              type="button"
                              onClick={() => {
                                if (inv.payment_url) {
                                  window.location.href = inv.payment_url;
                                  return;
                                }
                                void handlePayInvoice(inv.id);
                              }}
                              disabled={payingId === inv.id}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-lg bg-alsero-first text-white hover:bg-alsero-second transition-colors disabled:opacity-60"
                            >
                              {payingId === inv.id ? t('customer_panel.common.loading') : t('customer_panel.invoices.pay')}
                            </button>
                          )}
                          {inv.has_pdf && (
                          <button
                            type="button"
                            onClick={() => handleDownloadPdf(inv.id)}
                            disabled={downloadingId === inv.id}
                            className="text-blue-600 hover:text-blue-800 font-medium text-sm disabled:opacity-50"
                          >
                            {downloadingId === inv.id ? t('customer_panel.common.loading') : t('customer_panel.invoices.download_pdf')}
                          </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pageCount > 1 && (
                <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3">
                  <span className="text-sm text-gray-700">
                    {t('customer_panel.common.page')} {currentPage} / {pageCount} ({total} {t('customer_panel.invoices.total_count')})
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={currentPage <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className="rounded border border-gray-300 bg-white px-3 py-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      {t('customer_panel.common.previous')}
                    </button>
                    <button
                      type="button"
                      disabled={currentPage >= pageCount}
                      onClick={() => setPage((p) => p + 1)}
                      className="rounded border border-gray-300 bg-white px-3 py-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      {t('customer_panel.common.next')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
